import React, { useContext, useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import ListingContent from 'components/listing-content';
import ConnectionRequest from 'components/connection-request';
import { SoldListingExperimentCard } from 'components/sold-listing-experiment-card';
import Tabs from 'components/tabs';
import Tab from 'components/tabs/tab';
import Rooms from 'components/dynamic-page/listing-page/rooms';
import Description from 'components/dynamic-page/listing-page/description';
import KeyFacts from 'components/dynamic-page/listing-page/key-facts';
import Map, { TrackEventLocation } from 'components/swiper-carousel/map';
import { useIsNotMobile } from 'hooks/use-size-class';
import { IModalContext, ModalContext } from 'contexts/modal';
import ChartGroupLoading from 'components/charts/chart-helpers/chart-group-loading';
import LoadWhenVisible from 'components/load-when-visible';
import LoadingSkeleton from 'components/loading-skeleton';
import PriceHistory from 'components/price-history';
import creaAnalytics from 'utils/crea-analytics';
import { listingComponentHeights } from 'constants/listing-components';
import SimilarListingsSections from './similar-listings-sections';
import InternalLinks from 'components/home-page/internal-links';
import Breadcrumbs from 'components/breadcrumbs';
import SwiperCarousel from 'components/swiper-carousel';
import Zoopraisal from 'components/dynamic-page/listing-page/zoopraisal';
import { isListingVisibleToUser } from 'utils/listing-agreement-helper';
import { IUserContext, UserContext } from 'contexts/user';
import { listingPageModalIds } from 'constants/test-constants';
import { trackEvent } from 'utils/google-tag-manager';
import endpoint, { apiHeaders } from 'utils/endpoint';
import { checkIfEnoughData, endDate, startDate } from 'utils/data-vis-helpers';
import getZoopraisal, { Zoopraisal as IZoopraisal, ZoopraisalData } from 'utils/zoopraisal';
import { isCanadianProvinceCode } from 'utils/province_or_state';
import ProviderDisclaimer from '../../provider-disclaimer';
import Banner, { BannerContentType } from 'components/banner';
import FavouritePrompt from './favourite-prompt';
import { InView } from 'react-intersection-observer';
import { captureListingView } from 'pages/api/viewed-listings/viewedListingsApiClient';
import Cookies from 'js-cookie';
import { useExperiment, useGrowthBook } from '@growthbook/growthbook-react';
import { DEVICE_ID_COOKIE, MLML_AGENT_COOKIE_NAME } from 'constants/cookies';
import { GTM_CLICK_ADDRESS_PAGE_COMMUTE_TAB, GTM_CLICK_ADDRESS_PAGE_DEMOGRAPHICS_TAB, GTM_CLICK_ADDRESS_PAGE_DESCRIPTION_TAB,
  GTM_CLICK_ADDRESS_PAGE_MAP_TAB, GTM_CLICK_ADDRESS_PAGE_ROOMS_TAB,
  GTM_CLICK_ADDRESS_PAGE_SALES_ACTIVITY_TAB, GTM_VIEW_ADDRESS_PAGE_AVERAGE_SOLD_PRICE_TAB, GTM_VIEW_ADDRESS_PAGE_KEY_FACTS_TAB,
  GTM_VIEW_ADDRESS_PAGE_PRICE_HISTORY_TAB, GTM_VIEW_ADDRESS_PAGE_SCHOOLS_TAB, GTM_VIEW_ADDRESS_PAGE_ZOOPRAISAL,
  GTM_VIEW_ADDRESS_PAGE_SOLD_LISTING, GTM_VIEW_ADDRESS_PAGE_FOR_SALE_LISTING, GTM_VIEW_ADDRESS_PAGE_INACTIVE_LISTING } from 'constants/events';
import ConditionalWrapper from 'components/conditional-wrapper';
import { getFooterData } from './get-server-side-props';
import { useThemeContext } from 'contexts';
import { INACTIVE_STATUS, EXPIRED_STATUS, NOT_AVAILABLE_OTHER_STATUS } from 'contexts/preferences/listing-params';
import styles from './style.module.scss';
import { ExperimentVariants } from 'utils/types';
import MyLinkMyLeadBanner from 'components/my-link-my-lead-banner';
import { ThemeNames } from 'types/themes';
import MyLinkMyLeadCreate from 'components/my-link-my-lead-create';
import { buildClassName } from 'utils/build-class-name';
import { useListingRestrictions } from 'hooks/use-listing-restrictions';
import generateFullAddress from 'utils/get-full-address';
import LocationUnavailable from './location-unavailable';
import { HttpRequestMethodTypes } from 'types/HttpRequestMethodTypes';
import NearbySchools from 'components/dynamic-page/listing-page/nearby-schools';
import TravelTime from 'components/dynamic-page/listing-page/travel-time';
import NeighbourhoodDemographics from 'components/dynamic-page/listing-page/neighbourhood-demographics';

import type Listing from 'data/listing';
import type { Breadcrumb } from 'components/breadcrumbs';
import type { LinkDataType } from 'components/home-page/internal-links';
import type Point from 'data/geojson/point/type';
import type { ProvinceOrStateCode } from 'utils/province_or_state';
import type Address from 'data/addresses';
import { Stats } from 'data/stats';


interface Props {
  listing: Listing;
  listings: Listing[];
  breadcrumbs: Breadcrumb[];
  isCrawler: boolean;
  isCrawlerLighthouse: boolean;
  footerData: LinkDataType[];
  addressesData: Address[];
  chartData: Stats | null;
  neighbourhoodData: Stats | null;
  zoopraisalDataPramas: IZoopraisal | null;
  demographics: {
    schools: Array<Record<string, unknown>>;
    neighbourhood: Record<string, unknown> | null;
  };
}

export default function ListingPage({ listing, listings, breadcrumbs, isCrawler, footerData: initialFooterData, addressesData, chartData: initialChartData, neighbourhoodData: initialNeighbourhoodData, zoopraisalDataPramas, demographics }: Props) {
  const { isPrivate: listingIsPrivate, isRebgvVow, province, providerId, isSignInAndTermsOfUseRequired } = listing;
  const { isAuthenticated, acceptedBrowserBasedTerms, acceptedTerms, areZoocasaTermsRead, setZoocasaTermsStateToRead, hasNavigatedFromInternalLink, user } = useContext(UserContext) as IUserContext;
  const { themeName, theme } = useThemeContext();
  const isPrivate = !isAuthenticated && listingIsPrivate;
  const [showsListing, setShowsListing] = useState<boolean>(false);
  const [footerData, setFooterData] = useState<LinkDataType[]>(initialFooterData);
  const { openModal } = useContext(ModalContext) as IModalContext;
  const isNotMobile = useIsNotMobile();
  const topOfListingRef = useRef<HTMLDivElement>(null);
  const priceHistoryRef = useRef<HTMLDivElement>(null);
  const nonDeletedListings = listings.filter(listing => listing.status !== 'deleted');
  const isCanadianListing = isCanadianProvinceCode(province?.toLowerCase() as ProvinceOrStateCode);

  const { canViewSchools } = useListingRestrictions();

  const { marketStats } = listingComponentHeights;
  const [viewedKeyFacts, setViewedKeyFacts] = useState(false);
  const [viewedPriceHistory, setViewedPriceHistory] = useState(false);
  const [viewedAvgSoldPrice, setViewedAvgSoldPrice] = useState(false);
  const [viewedZoopraisal, setViewedZoopraisal] = useState(false);
  const deviceId = Cookies.get(DEVICE_ID_COOKIE);
  const guideInfoDefaultExpanded = isCrawler;
  const [isSimilarListingsVisible, setIsSimilarListingsVisible] = useState(isCrawler);
  const [isPriceHistoryVisible, setIsPriceHistoryVisible] = useState(isCrawler);
  const [isDisclaimerVisible, setIsDisclaimerVisible] = useState(isCrawler);
  const [isZoopraisalVisible, setIsZoopraisalVisible] = useState(isCrawler);
  const showsMLMLCreate = themeName && themeName === ThemeNames.EXP_REALTY_US && !!Cookies.get(MLML_AGENT_COOKIE_NAME);
  const showsMLML = themeName && themeName === ThemeNames.EXP_REALTY_US && !showsMLMLCreate;

  const { ready: isSoldPvrButtonsExpReady } = useGrowthBook();
  const isSoldListingExperimentEnabled = listing.isSold;
  const abSoldListingExperiment = useExperiment({ key: 'sold-listing-PVR-vs-home-estimate', variations: [0, 1], weights: [.5, .5], active: isSoldListingExperimentEnabled });

  // Track listing status GTM event
  useEffect(() => {
    const trackListingStatus = () => {
      if (listing.isSold) {
        trackEvent(GTM_VIEW_ADDRESS_PAGE_SOLD_LISTING);
      }
      else if (listing.isAvailable) {
        trackEvent(GTM_VIEW_ADDRESS_PAGE_FOR_SALE_LISTING);
      }
      else if (listing.isInactive) {
        trackEvent(GTM_VIEW_ADDRESS_PAGE_INACTIVE_LISTING);
      }
    };
    trackListingStatus();
  }, [listing.isAvailable, listing.isSold, listing.status, listing.isInactive]);

  // charts
  const [isChartVisible, setIsChartVisible] = useState(initialChartData ? true : false);
  const [isChartGroupLoading, setIsChartGroupLoading] = useState(true);
  const [chartData, setChartData] = useState(initialChartData);
  const [neighbourhoodData, setNeighbourhoodData] = useState(initialNeighbourhoodData);
  const chartContainerHeight = () => {
    if (!chartData && !isChartGroupLoading) {
      return marketStats.noData;
    }
    return isNotMobile ? marketStats.desktop : marketStats.mobile;
  };
  // school/mortgage/neighbourhood/commute tabs
  const [isSchoolTabsVisible, setIsSchoolTabsVisible] = useState(false);
  const [hasSchools, setHasSchools] = useState<boolean>(true);
  const [hasFetchedSchools, setHasFetchedSchools] = useState(false);
  const [zoopraisalData, setZoopraisalData] = useState<IZoopraisal | null>(zoopraisalDataPramas ? new ZoopraisalData(zoopraisalDataPramas) : null);
  const excludedZoopraisalAddressSlugs = ['45-sunnycrest-dr-nepean-on'];
  const shouldShowZoopraisal = !listing.isRental && listing.isAvailable && zoopraisalData?.isAvailable && !excludedZoopraisalAddressSlugs.includes(listing.addressSlug);

  const footerHeading = isNotMobile ? '' : 'Explore More Listings';
  const [lazyLoadedFooterLink, setLazyLoadedFooterLink] = useState(false);

  const fetchZoopraisal = async () => setZoopraisalData(await getZoopraisal({ listing }));

  useEffect(() => {
    if (lazyLoadedFooterLink && !initialFooterData.length) {
      getFooterData(addressesData, themeName).then(r => setFooterData(r || []));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lazyLoadedFooterLink]);

  useEffect(() => {
    if (providerId) {
      setShowsListing(isListingVisibleToUser(isPrivate, providerId, acceptedBrowserBasedTerms, acceptedTerms));
    }
  }, [isPrivate, providerId, acceptedBrowserBasedTerms, acceptedTerms]);

  const isREBGVBlur = isRebgvVow && !showsListing;

  const openListingModalIfContainsIdHash = () => {
    if (window.location.hash) {
      const id = Number(window.location.hash.replace('#', ''));
      openModal('listing', { listingId: id });
    }
  };

  // When the user scrolls to the key facts, track the GTM event
  const trackKeyFactsView = (isVisible: boolean) => {
    if (isVisible && !viewedKeyFacts) {
      setViewedKeyFacts(true);
      trackEvent(GTM_VIEW_ADDRESS_PAGE_KEY_FACTS_TAB);
    }
  };

  const trackPriceHistoryView = (isVisible: boolean) => {
    if (isVisible && !viewedPriceHistory) {
      setViewedPriceHistory(true);
      trackEvent(GTM_VIEW_ADDRESS_PAGE_PRICE_HISTORY_TAB);
    }
  };

  const trackAvgSoldPriceView = (isVisible: boolean) => {
    if (isVisible && !viewedAvgSoldPrice) {
      setViewedAvgSoldPrice(true);
      trackEvent(GTM_VIEW_ADDRESS_PAGE_AVERAGE_SOLD_PRICE_TAB);
    }
  };

  const trackZoopraisalView = (isVisible: boolean) => {
    if (isVisible && !viewedZoopraisal) {
      setViewedZoopraisal(true);
      trackEvent(GTM_VIEW_ADDRESS_PAGE_ZOOPRAISAL);
    }
  };

  useEffect(() => {
    const abortControllerMunicipality = new AbortController();
    const abortControllerNeighbourhood = new AbortController();
    if (isChartVisible && !chartData) {
      setIsChartGroupLoading(true);

      const request = {
        municipality: listing?.city?.toLowerCase(),
        dateStart: startDate,
        dateEnd: endDate,
        homeType: '',
      };
      endpoint(
        '/stats',
        HttpRequestMethodTypes.GET,
        request,
        apiHeaders(['Authorization']),
        abortControllerMunicipality.signal,
        'https://stats.zoocasa.com'
      ).then((response: any) => {
        if (checkIfEnoughData(response.data)) {
          setChartData(response);
        }
        if (listing.neighbourhoodName) {
          const request = {
            dateStart: startDate,
            dateEnd: endDate,
            municipality: listing.city?.toLowerCase(),
            neighbourhood: listing.neighbourhoodName.toLowerCase(),
          };
          endpoint(
            '/stats',
            HttpRequestMethodTypes.GET,
            request,
            apiHeaders(['Authorization']),
            abortControllerNeighbourhood.signal,
            'https://stats.zoocasa.com'
          ).then((response: any) => {
            if (response.data && checkIfEnoughData(response.data || [])) {
              setNeighbourhoodData(response);
            }
          });
        }
      }).catch(() => {
        setChartData(undefined);
      });
      setIsChartGroupLoading(false);
    }

    return () => {
      // Abort the request when the component unmounts or when a dependency changes
      abortControllerMunicipality?.abort();
      abortControllerNeighbourhood?.abort();
    };

  }, [chartData, isChartVisible, listing?.city, listing?.neighbourhoodName]);

  useEffect(() => {
    if (isSchoolTabsVisible) {
      trackEvent(GTM_VIEW_ADDRESS_PAGE_SCHOOLS_TAB);
    }
  }, [isSchoolTabsVisible]);

  useEffect(() => {
    if (!listing.isSold && !zoopraisalData) {
      fetchZoopraisal();
    }
    trackEvent('UiAddresspView');
    if (showsMLML) {
      trackEvent('UiAddressMLMLView');
    }
    openListingModalIfContainsIdHash();
    if (listing.isCrea) {
      creaAnalytics(listing.id, listing.url);
    }
    if (!isNotMobile) {
      trackEvent('Mobile_Listing_Page_View');
    }

    if (isAuthenticated && listing.isSold) {
      trackEvent('UiAddressSoldAuthView');
    }
    if (!isAuthenticated && listing.isSold) {
      trackEvent('UiAddressSoldNonAuthView');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Run experiment for SOLD listings only on address page
    if (isSoldListingExperimentEnabled && isSoldPvrButtonsExpReady) {
      switch (abSoldListingExperiment.variationId) {
      case ExperimentVariants.A:
        trackEvent('XpA_AddressOnlineEstButView');
        break;
      case ExperimentVariants.B:
        trackEvent('XpB_AddressOfflineEstButView');
        break;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSoldPvrButtonsExpReady]);

  useEffect(() => {
    if (deviceId) {
      captureListingView({
        listingId: listing.id,
        userId: isAuthenticated && user ? user.id : deviceId,
        isModal: false,
        authUser: isAuthenticated,
        sourceTheme: themeName,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  useEffect(() => {
    if (isSignInAndTermsOfUseRequired && isAuthenticated && !acceptedTerms && !showsListing) {
      openModal('terms-of-use', { providerId });
    }
  }, [isAuthenticated, acceptedTerms, listing.isSignInAndTermsOfUseRequired, providerId, openModal, isSignInAndTermsOfUseRequired, showsListing]);

  const { getStreet, city } = listing;
  const hasCoordinates = listing.position?.coordinates?.length === 2;
  const renderSchoolTab = [undefined, true].includes(hasSchools) || canViewSchools;
  const renderDemographicsTab = (!!demographics?.neighbourhood) || listing.neighbourhood?.id && isCanadianListing;
  const renderCommuteTab = hasCoordinates;
  // for schools, demographics, and commute tabs
  const hasAtLeastOneTab = renderSchoolTab || renderDemographicsTab || renderCommuteTab;
  const street = getStreet(true);
  const addressText = generateFullAddress(street, city, province, false) as string;

  return (
    <div ref={topOfListingRef}>
      {listing.isCrea && !areZoocasaTermsRead &&
        <Banner content={BannerContentType.CREA} onDismiss={() => setZoocasaTermsStateToRead()} />
      }
      <Breadcrumbs breadcrumbs={breadcrumbs} />
      <SwiperCarousel listingData={{ listing }} isCrawler={isCrawler} />
      <section className={styles.component}>
        <div className={styles.wrapper}>
          <div className={styles['listing-content']}>
            <div className={styles['left-section']}>
              <ListingContent listing={listing} isVisible={showsListing} priceHistoryRef={priceHistoryRef} shouldScramble={isREBGVBlur} zoopraisalData={zoopraisalData} listings={nonDeletedListings} showsInfoIcon={true} soldListingExperimentId={isSoldListingExperimentEnabled ? abSoldListingExperiment.variationId : undefined} />
              <Tabs testId={ listingPageModalIds.listingKeyInfo }>
                <Tab label="Key Facts" testId={listingPageModalIds.listingKeyFactsTab}>
                  <InView onChange={trackKeyFactsView}>
                    <KeyFacts listing={listing} isVisible={showsListing} shouldScramble={isREBGVBlur} />
                  </InView>
                </Tab>
                <Tab label="Description" testId={listingPageModalIds.listingDescriptionTab} gtmTrackEvent={() => trackEvent(GTM_CLICK_ADDRESS_PAGE_DESCRIPTION_TAB)}><Description listing={listing} isVisible={showsListing} isCrawler={isCrawler} shouldScramble={isREBGVBlur}/></Tab>
                {listing.rooms?.length ? <Tab label="Rooms" testId={listingPageModalIds.listingRoomsTab} gtmTrackEvent={() => trackEvent(GTM_CLICK_ADDRESS_PAGE_ROOMS_TAB)}><Rooms listing={listing} isVisible={showsListing} shouldScramble={isREBGVBlur} /></Tab> : null}
                <Tab label="Map" isFullScreen={true} testId={listingPageModalIds.listingMapTab} gtmTrackEvent={() => trackEvent(GTM_CLICK_ADDRESS_PAGE_MAP_TAB)}>
                  { hasCoordinates ?
                    <Map listingId={listing!.id+'-tab'} position={(listing.position as unknown as Point)} address={addressText} className={styles.map} isInteractive={isNotMobile} loadWhenVisible={false} trackEventLocation={TrackEventLocation.Page} />
                    :
                    <LocationUnavailable imagePath={theme.style_overrides?.locationUnavailable?.imagePath} message={theme.style_overrides?.locationUnavailable?.message} />
                  }
                </Tab>
              </Tabs>
              {!hasNavigatedFromInternalLink && hasCoordinates && <SimilarListingsSections listing={listing} hasNavigatedFromInternalLink={hasNavigatedFromInternalLink} isTop={true} />}
              {!isPriceHistoryVisible && <div ref={priceHistoryRef} className={styles['price-history-placeholder']}></div>}
              <ConditionalWrapper condition={!isCrawler} wrapper={children => <LoadWhenVisible onValueChange={() => setIsPriceHistoryVisible(true)}>{children}</LoadWhenVisible>}>
                {isPriceHistoryVisible ?
                  <InView onChange={trackPriceHistoryView}>
                    <PriceHistory isVisible={isCrawler || showsListing} priceHistoryRef={priceHistoryRef} listing={listing} listings={nonDeletedListings} />
                  </InView> : <></>}
              </ConditionalWrapper>
              <ConditionalWrapper condition={!isCrawler} wrapper={children => <LoadWhenVisible onValueChange={() => setIsDisclaimerVisible(true)}>{children}</LoadWhenVisible>}>
                {isDisclaimerVisible ? <ProviderDisclaimer providerId={listing.providerId} listing={listing} tenantName={themeName} /> : <></>}
              </ConditionalWrapper>
              <ConditionalWrapper condition={!isCrawler} wrapper={children => <LoadWhenVisible onValueChange={() => setIsDisclaimerVisible(true)}>{children}</LoadWhenVisible>}>
                {isDisclaimerVisible && showsMLML && !isNotMobile ? <MyLinkMyLeadBanner /> : <></>}
              </ConditionalWrapper>
              {shouldShowZoopraisal && zoopraisalData?.isAvailable &&
                <ConditionalWrapper condition={!isCrawler} wrapper={children => <LoadWhenVisible onValueChange={() => setIsZoopraisalVisible(true)}>{children}</LoadWhenVisible>}>
                  {isZoopraisalVisible ?
                    <InView onChange={trackZoopraisalView}>
                      <Zoopraisal topOfListingRef={topOfListingRef} listing={listing} isPrivate={isPrivate} shouldScramble={isREBGVBlur} zoopraisalData={zoopraisalData} />
                    </InView> : <></>}
                </ConditionalWrapper>
              }
              <ConditionalWrapper condition={!isCrawler} wrapper={children => <LoadWhenVisible onValueChange={() => setIsChartVisible(true)}>{children}</LoadWhenVisible>}>
                <div style={{ minHeight: chartContainerHeight() }}>
                  {isChartVisible && chartData &&
                    <Tabs testId={listingPageModalIds.charts} className={styles['smdc-tabs']}>
                      <Tab label="Avg. Sold Price" testId={listingPageModalIds.avgSoldPriceTab}>
                        <InView onChange={trackAvgSoldPriceView}>
                          <ChartGroupLoading
                            testId={listingPageModalIds.avgSoldPriceTab}
                            listing={listing}
                            isBlurred={isREBGVBlur}
                            type="market-stats"
                            chartData={chartData}
                            neighbourhoodData={neighbourhoodData}
                          />
                        </InView>
                      </Tab>
                      <Tab label="Sales Activity" testId={listingPageModalIds.salesActivityTab} gtmTrackEvent={() => trackEvent(GTM_CLICK_ADDRESS_PAGE_SALES_ACTIVITY_TAB)}>
                        <ChartGroupLoading
                          testId={listingPageModalIds.salesActivityTab}
                          listing={listing}
                          isBlurred={isREBGVBlur}
                          type="market-competition"
                          chartData={chartData}
                          neighbourhoodData={neighbourhoodData}
                        />
                      </Tab>
                    </Tabs>
                  }
                </div>
              </ConditionalWrapper>
              <ConditionalWrapper condition={!isCrawler} wrapper={children => <LoadWhenVisible onValueChange={() => setIsSchoolTabsVisible(true)}>{children}</LoadWhenVisible>}>
                {isCrawler || (isSchoolTabsVisible && hasAtLeastOneTab) ?
                  <Tabs testId={listingPageModalIds.secondaryInfo} className={styles['smdc-tabs']}>
                    { renderSchoolTab ? <Tab label="Schools" testId={listingPageModalIds.listingSchoolsTab}>
                      <NearbySchools hasFetched={hasFetchedSchools} setHasFetched={setHasFetchedSchools} setHasSchools={setHasSchools} listing={listing} schools={demographics.schools}/>
                    </Tab> : null}
                    { renderDemographicsTab ? <Tab label="Demographics" testId={listingPageModalIds.listingDemographicsTab} gtmTrackEvent={() => trackEvent(GTM_CLICK_ADDRESS_PAGE_DEMOGRAPHICS_TAB)}><NeighbourhoodDemographics neighbourhoodId={listing.neighbourhood?.id} neighbourhood={demographics.neighbourhood} /></Tab> : null}
                    { renderCommuteTab ? <Tab label="Commute" testId={listingPageModalIds.listingCommuteTab} gtmTrackEvent={() => trackEvent(GTM_CLICK_ADDRESS_PAGE_COMMUTE_TAB)}><TravelTime listing={listing} /></Tab> : null}
                  </Tabs>
                  : <LoadingSkeleton />
                }
              </ConditionalWrapper>
            </div>
            {isNotMobile &&
              <div className={buildClassName(styles['request-info-section'], showsMLMLCreate && styles['request-info-section-lowered'])}>
                {showsMLMLCreate ?
                  <MyLinkMyLeadCreate listing={listing} />
                  :
                  <div>
                    {isSoldListingExperimentEnabled ? (
                      <SoldListingExperimentCard mode={abSoldListingExperiment.variationId as ExperimentVariants} listing={listing} />
                    ) : (
                      <>
                        <ConnectionRequest listing={listing} isAddressPage={true} />
                        <FavouritePrompt listingId={listing.id} addressSlug={null} isAuthenticated={isAuthenticated}/>

                      </>)
                    }
                    {showsMLML && <MyLinkMyLeadBanner />}
                  </div>
                }
              </div>
            }
          </div>
        </div>
        <ConditionalWrapper condition={!isCrawler} wrapper={children => <LoadWhenVisible onValueChange={() => setIsSimilarListingsVisible(true)}>{children}</LoadWhenVisible>}>
          {isSimilarListingsVisible && hasCoordinates ? <SimilarListingsSections listing={listing} hasNavigatedFromInternalLink={hasNavigatedFromInternalLink} /> : <></>}
        </ConditionalWrapper>
      </section>
      <section>
        <ConditionalWrapper
          condition={!guideInfoDefaultExpanded || !isCrawler}
          wrapper={children =>
            <LoadWhenVisible onValueChange={() => setLazyLoadedFooterLink(true)}>{lazyLoadedFooterLink && children}</LoadWhenVisible>
          }>
          <InternalLinks isActive={isCrawler} data={footerData} heading={footerHeading} />
        </ConditionalWrapper>
      </section>
    </div>
  );
}